import { compose, mapProps, withProps } from 'recompose';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';

import type { EnrollmentChoices } from 'bundles/enroll-common/components/xdp/withEnrollment';
import { createContainer } from 'bundles/naptimejs';
import ProductSkuV1 from 'bundles/naptimejs/resources/productSkus.v1';
import type S12nDerivativesV1 from 'bundles/naptimejs/resources/s12nDerivatives.v1';
import {
  isOwnableProductsEnabled,
  withFindOwnableProductsByUnderlyingProductItemId,
} from 'bundles/ownable-products-common/hooks/useFindOwnableProducts';
import type { PropsFromFindByUnderlyingProductItemId } from 'bundles/ownable-products-common/hooks/useFindOwnableProducts';
import { CREDENTIAL_TRACK_SUBSCRIPTION } from 'bundles/payments-common/common/ProductType';

export type PropsToWithPrice = {
  enrollmentAvailableChoices?: EnrollmentChoices;
  s12nId?: string;
};

type PropsFromWithPriceNaptime = {
  s12nDerivative?: S12nDerivativesV1;
  multiS12nDerivatives?: Array<S12nDerivativesV1>;
};

export type PropsFromWithPrice = PropsFromWithPriceNaptime;

type PropsToWithCredentialTrackProductSkus = {
  id: string;
};

export type ProductSku = {
  id: string;
  underlyingProductId?: string;
  productType: string;
  numberOfCycles?: number | null;
  subscriptionType?: string | null;
};

export type PropsFromWithCredentialTrackProductSkus = {
  productSkus?: ProductSku[];
};

type PropsFromWithProps = { underlyingProductItemId?: string };

type PropsFromProductSkusNaptime = {
  productSkus?: ProductSkuV1[];
};

const withCredentialTrackProductSKUs = <InputProps extends PropsToWithCredentialTrackProductSkus>() =>
  compose<PropsFromWithCredentialTrackProductSkus, InputProps>(
    // This underlyingProductItemId is only used to get passed to `withFindOwnableProductsByUnderlyingProductItemId`
    withProps<PropsFromWithProps, InputProps>(({ id }) => {
      if (isOwnableProductsEnabled()) {
        return { underlyingProductItemId: id };
      }

      return {};
    }),
    withFindOwnableProductsByUnderlyingProductItemId,
    createContainer<PropsFromWithCredentialTrackProductSkus, InputProps>(({ id }) => {
      if (!isOwnableProductsEnabled() && id) {
        return {
          productSkus: ProductSkuV1.finder('findByUnderlying', {
            params: {
              id: tupleToStringKey([CREDENTIAL_TRACK_SUBSCRIPTION, id]),
            },
            fields: ['id', 'productType', 'properties'],
          }),
        };
      } else {
        return {};
      }
    }),
    mapProps<
      PropsFromWithCredentialTrackProductSkus,
      PropsFromWithProps & PropsFromFindByUnderlyingProductItemId & PropsFromProductSkusNaptime
      // Destructure `underlyingProductItemId` so it doesn't returned back to the consuming component
    >(({ ownableProducts, productSkus, underlyingProductItemId, ...props }) => {
      const formattedOwnableProducts = ownableProducts?.length
        ? ownableProducts?.map(({ productType, productItemId, numberOfInstallments, ...rest }) => {
            return {
              id: tupleToStringKey([productType ?? '', productItemId ?? '']),
              productType: productType ?? '',
              numberOfCycles: numberOfInstallments,
              underlyingProductId: tupleToStringKey([CREDENTIAL_TRACK_SUBSCRIPTION, underlyingProductItemId ?? '']),
              ...rest,
            };
          })
        : undefined;

      const sortedData = (formattedOwnableProducts ?? productSkus)?.sort((skuA, skuB) => {
        // Need to set a number by default because 'properties' is possibly null/undefined
        const cyclesA = skuA?.numberOfCycles ?? 0;
        const cyclesB = skuB?.numberOfCycles ?? 0;
        return cyclesA - cyclesB;
      });

      return {
        ...props,
        productSkus: sortedData,
      };
    })
  );

export { withCredentialTrackProductSKUs };
